import { Button, ButtonGroup } from "@msuite/ui/button";
import { FileUpload, useFileUpload } from "@msuite/ui/file-upload";
import { FormCheckbox, Textarea } from "@msuite/ui/form";
import { useDisclosure, useHotkey } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { scrollIntoView } from "@msuite/ui/utils";
import { hasNoteContent } from "@msuite/utils/notes";
import { type PostNoteForm, postNoteSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import type { NotesTable } from "@msuite/utils/types";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useUpload } from "@/api/files";
import { FileCardRemote } from "@/components/files";
import type { NotesProps } from "@/components/notes/types";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { MarkedAsReplyHint } from "./marked-as-reply-hint";

/** Props Interface */
interface NoteFormProps extends Pick<NotesProps, "postFn"> {
	refetch: () => Promise<void>;
	markedAsReply: string | null;
	resetMarkedAsReply: (_null: string | null) => void;
	notesTable: NotesTable;
	identifier: string;
	withIsImportant?: boolean;
	isImportantLabel?: string;
	withIsImportantIsDisabled?: boolean;
}

export function NoteForm({
	postFn,
	refetch,
	markedAsReply,
	resetMarkedAsReply,
	notesTable,
	identifier,
	withIsImportant,
	isImportantLabel,
	withIsImportantIsDisabled = false,
}: NoteFormProps) {
	/** State */
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const fileUploaderDisclosure = useDisclosure();
	const { uploadFn } = useUpload();
	const controller = useFileUpload({
		uploadFn,
	});

	/** Hooks */
	const mentionOptions = useMentionOptions();
	const methods = useForm({
		defaultValues: {
			note: "",
			isImportant: false,
		},
		resolver: resolver(postNoteSchema),
	});
	const { t } = useTranslation();

	/** Functions */
	async function handleOnSubmit(json: PostNoteForm) {
		// The button is already disabled here, and the hotkey below is not — Ctrl+Enter reaches the
		// submit whatever the button says, so the rule is checked rather than assumed.
		if (!canSubmit) return;
		const uploadResults = await controller.handleOnFileUpload();
		if (markedAsReply) json.parentNoteId = markedAsReply;
		const note = await postFn(json, uploadResults);
		if (note) {
			scrollIntoView({
				id: note.id,
				timeout: 400,
				count: 0,
				behavior: "smooth",
			});
		}
		handleOnReset();
		await refetch();
		resetMarkedAsReply(null);
	}

	function handleOnReset() {
		methods.reset({ note: "" });
		controller.handleOnReset();
		fileUploaderDisclosure.onClose();
	}

	/** Effects */
	useHotkey(
		"Enter",
		async () => {
			if (methods.formState.isSubmitting) return;
			await methods.handleSubmit(handleOnSubmit)();
		},
		{
			ctrlOrCmd: true,
			enabled: isFocused,
		},
	);

	/** Computed */
	// A note is text or attachments and needs only one of the two: a photograph of a blocked access
	// route says what it needs to without a sentence beside it. Neither is nothing, which is the one
	// state the send button is closed in — the same rule `Notes.insertNote` refuses on.
	const canSubmit = hasNoteContent({
		note: methods.watch("note"),
		fileCount: controller.files.length,
	});

	/** Render */
	return (
		<form
			className="flex flex-col gap-2 -mx-2 -mb-1.75"
			onSubmit={(e) => {
				e.stopPropagation();
				methods.handleSubmit(handleOnSubmit)(e);
			}}
			data-cy="note-form"
		>
			<MarkedAsReplyHint
				markedAsReply={markedAsReply}
				resetMarkedAsReply={resetMarkedAsReply}
				notesTable={notesTable}
				identifier={identifier}
			/>
			<Textarea
				control={methods.control}
				name="note"
				label={undefined}
				isRequired={false}
				className="max-h-[100px]"
				placeholder={t("placeholders.add_note")}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				{...mentionOptions}
				onKeyUp={(event) => {
					if (event.key === "Escape") {
						resetMarkedAsReply(null);
					}
				}}
				data-cy="note-form-textarea"
			/>
			{fileUploaderDisclosure.isOpen && (
				<div className="!bg-primaryGray-50 dark:!bg-primaryGray-800 p-0.5 border-[0.5px] rounded-2xl card">
					<FileUpload
						withLabel={false}
						controller={controller}
						renderFile={(file) => (
							<FileCardRemote
								fileId={file.id}
								onInteraction={async () => {}}
							/>
						)}
					/>
				</div>
			)}
			<div className="justify-between items-center h-stack">
				<div>
					{withIsImportant && (
						<FormCheckbox
							control={methods.control}
							name="isImportant"
							isDisabled={withIsImportantIsDisabled}
						>
							{guardString(isImportantLabel)}
						</FormCheckbox>
					)}
				</div>
				<ButtonGroup isAttached={false}>
					<Button
						variant="white"
						icon={<Icon name="Paperclip" />}
						onClick={fileUploaderDisclosure.onToggle}
						data-cy="note-form-file-toggle"
					/>
					<Button
						variant="primary"
						type="submit"
						isDisabled={!canSubmit}
						isLoading={methods.formState.isSubmitting}
					>
						{t("general.send")}
					</Button>
				</ButtonGroup>
			</div>
		</form>
	);
}
